import React from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ArrowRight, ExternalLink } from 'lucide-react';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const scrollToVision = () => {
    const element = document.getElementById('vision');
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image - Premium Collectibles Composition */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://customer-assets.emergentagent.com/job_create-site-48/artifacts/kmeht2bt_84368538-15C8-47B8-B503-E72462C3B1AA.png"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        {/* Enhanced overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/60 to-black/70" />
      </div>

      {/* Content */}
      <div className="container relative z-10 py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left: Hero Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white order-2 lg:order-1"
          >
            <p className="uppercase tracking-[0.22em] text-xs text-white/80 mb-4">
              Private Investor Room
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] mb-4 leading-tight">
              SOVEREIGN: Where Serious Markets Converge
            </h1>
            <p className="text-base sm:text-lg text-white/90 mb-2 leading-relaxed font-medium">
              We're not building another marketplace.
            </p>
            <p className="text-base sm:text-lg text-white/90 mb-6 leading-relaxed">
              We're building the <span className="font-semibold text-accent">trust infrastructure</span> that makes a $400B+ market liquid.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                onClick={scrollToContact}
                className="bg-accent text-black hover:bg-accent/90 font-semibold"
                data-testid="hero-primary-cta"
              >
                Request Investor Pack
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={scrollToVision}
                className="border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm"
                data-testid="hero-secondary-cta"
              >
                See The Opportunity
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.div>

          {/* Right: Key Numbers Card with Animated Counters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <Card className="p-6 sm:p-8 bg-card/95 backdrop-blur-sm border-accent/20">
              <h3 className="text-sm uppercase tracking-[0.22em] text-muted-foreground mb-6">
                Investment Highlights
              </h3>
              <div className="space-y-6">
                <div>
                  <p className="text-3xl sm:text-4xl font-bold tabular-nums mb-1">
                    $<CountUp end={400} duration={2.5} />B+
                  </p>
                  <p className="text-sm text-muted-foreground">Global Collectibles Market TAM</p>
                </div>
                <div className="section-divider" />
                <div>
                  <p className="text-3xl sm:text-4xl font-bold tabular-nums mb-1">
                    $<CountUp end={2.5} decimals={1} duration={2.5} />M
                  </p>
                  <p className="text-sm text-muted-foreground">Seed Round — Open Now</p>
                </div>
                <div className="section-divider" />
                <div>
                  <p className="text-3xl sm:text-4xl font-bold tabular-nums mb-1">
                    $<CountUp end={500} duration={2.5} />M–$<CountUp end={1000} duration={2.5} separator="" />M
                  </p>
                  <p className="text-sm text-muted-foreground">Year 3 GMV Target</p>
                </div>
                <div className="section-divider" />
                <div className="pt-2">
                  <p className="text-xs font-medium text-accent mb-1">✓ May 2026 Launch Secured</p>
                  <p className="text-xs font-medium text-accent">✓ Prestigious Commitments In-Hand</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
